import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Edit,
  MapPin,
  Plus,
  RefreshCw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import {
  useGetDispatchCenters,
  useCreateDispatchCenter,
  useUpdateDispatchCenter,
  useDeleteDispatchCenter,
  type DispatchCenterListItem,
  getDispatchCenterCodeConflict,
} from "@/api/dispatch-centers";
import type { DispatchRunSchedule } from "@/api.types.v2";
import {
  DispatchAddressSearch,
  type SelectedAddress,
} from "./dispatch-address-search";
import { RunScheduleEditor } from "./run-schedule-editor";
import { DispatchCenterDropoffConfig } from "./dispatch-center-dropoff-config";
import { useGetCarriers } from "@/api/carriers";
import { useGetServiceAreas } from "@/api/service-areas";
import {
  useCarrierScope,
  useCenterScopeNotApplicable,
} from "@/contexts/carrier-scope-context";

/**
 * Pricing-redesign: DispatchCenter admin screen.
 *
 * Create and repair addresses through a complete Places selection.
 * Center identity and related operating configuration survive address repair.
 */

interface CreateState {
  code: string;
  name: string;
  operatorCarrierId: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressProvince: string;
  addressPostalCode: string;
  addressCountry: string;
  addressLocation: { lat: number; lng: number } | null;
  serviceAreaId: string;
  postalCodesText: string; // comma-separated FSAs
  isActive: boolean;
}

interface EditState {
  id: string;
  providerCarrierId: string;
  address: DispatchCenterListItem["address"];
  replacementAddress: SelectedAddress | null;
  addressLine2: string;
  name: string;
  postalCodesText: string;
  isActive: boolean;
  /** Delivery estimates config */
  processingMinutesText: string;
  runSchedule: DispatchRunSchedule | null;
}

const DEFAULT_CREATE: CreateState = {
  code: "",
  name: "",
  operatorCarrierId: "",
  addressLine1: "",
  addressLine2: "",
  addressCity: "",
  addressProvince: "",
  addressPostalCode: "",
  addressCountry: "CA",
  addressLocation: null,
  serviceAreaId: "",
  postalCodesText: "",
  isActive: true,
};

export function DispatchCentersScreen() {
  // This screen IS the dispatch-center roster; the top-bar center picker
  // would compete with it (pinning a center while editing the list of
  // centers is confusing). Disable the picker here.
  useCenterScopeNotApplicable();
  const [createState, setCreateState] = useState<CreateState | null>(null);
  const [createAttempted, setCreateAttempted] = useState(false);
  const [codeConflict, setCodeConflict] = useState<{
    code: string;
    message: string;
  } | null>(null);
  const [createFailure, setCreateFailure] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const {
    data: allDispatches,
    isLoading,
    refetch,
  } = useGetDispatchCenters({ includeInactive: true });
  // Carrier picker narrows the list to dispatches whose operator/provider
  // is in the selected carrier set. Server filter could replace this when
  // added. Empty selection = nothing (match the global "empty = nothing"
  // semantic).
  const { carrierIds: scopedCarrierIds } = useCarrierScope();
  const dispatches = useMemo(() => {
    const all = allDispatches ?? [];
    if (scopedCarrierIds.length === 0) return [];
    const set = new Set(scopedCarrierIds);
    return all.filter((d) => set.has(d.pickupProviderCarrier.id));
  }, [allDispatches, scopedCarrierIds]);

  const carriersQuery = useGetCarriers();
  const carriers = carriersQuery.data?.data ?? [];
  const selfRatedCarriers = carriers.filter((c) => c.selfRated);

  const serviceAreasQuery = useGetServiceAreas();
  const serviceAreas = serviceAreasQuery.data;
  const createAddressPostalCode = createState?.addressPostalCode ?? "";
  const createServiceAreaId = createState?.serviceAreaId ?? "";

  useEffect(() => {
    if (createServiceAreaId) return;
    const fsa = createAddressPostalCode.trim().slice(0, 3).toUpperCase();
    if (!fsa || !serviceAreas) return;
    const sa = serviceAreas.find((a) => a.postcodes?.includes(fsa));
    if (sa) {
      setCreateState((prev) =>
        prev ? { ...prev, serviceAreaId: sa.id } : prev
      );
    }
  }, [createAddressPostalCode, createServiceAreaId, serviceAreas]);

  const createMutation = useCreateDispatchCenter({
    onSuccess: () => {
      toast.success("Created", { description: "Dispatch center created" });
      void refetch();
      setCreateState(null);
    },
    onError: (e, input) => {
      const message = getDispatchCenterCodeConflict(e);
      if (message) {
        setCodeConflict({ code: input.code, message });
      } else {
        setCreateFailure(e.message);
        toast.error("Create failed", { description: e.message });
      }
    },
  });

  const updateMutation = useUpdateDispatchCenter({
    onSuccess: () => {
      toast.success("Saved", { description: "Dispatch center updated" });
      void refetch();
      setEditState(null);
    },
    onError: (e) => {
      toast.error("Save failed", { description: e.message });
    },
  });

  const deleteMutation = useDeleteDispatchCenter({
    onSuccess: () => {
      toast.success("Deleted", { description: "Dispatch center deleted" });
      void refetch();
      setDeleteId(null);
    },
    onError: (e) => {
      toast.error("Delete failed", { description: e.message });
    },
  });

  const createErrors = createState
    ? {
        code: !createState.code.trim()
          ? "Enter a code."
          : codeConflict?.code === createState.code.trim()
            ? codeConflict.message
            : null,
        name: createState.name.trim() ? null : "Enter a name.",
        operator: createState.operatorCarrierId
          ? null
          : "Choose an operator carrier.",
        address:
          createState.addressLine1.trim() &&
          createState.addressCity.trim() &&
          createState.addressProvince.trim() &&
          createState.addressCountry.trim() &&
          createState.addressLocation
            ? null
            : "Select an address from the search results.",
        area: createState.serviceAreaId ? null : "Choose a service area.",
      }
    : {};

  const handleCreate = () => {
    if (!createState || addressLoading) return;
    setCreateAttempted(true);
    if (Object.values(createErrors).some(Boolean)) return;
    setCreateFailure(null);
    const postalCodes = createState.postalCodesText
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    createMutation.mutate({
      code: createState.code.trim(),
      name: createState.name.trim(),
      operatorCarrierId: createState.operatorCarrierId,
      address: {
        line1: createState.addressLine1,
        line2: createState.addressLine2 || null,
        city: createState.addressCity,
        province: createState.addressProvince,
        country: createState.addressCountry,
        postalCode: createState.addressPostalCode || null,
        location: createState.addressLocation,
      },
      serviceAreaId: createState.serviceAreaId,
      postalCodes,
      isActive: createState.isActive,
    });
  };

  const handleCreateAddressChange = (address: SelectedAddress) => {
    setCreateState((prev) =>
      prev
        ? {
            ...prev,
            addressLine1: address.line1,
            addressLine2: address.line2,
            addressCity: address.city,
            addressProvince: address.province,
            addressPostalCode: address.postalCode,
            addressCountry: address.country,
            addressLocation: address.location,
            serviceAreaId: "",
          }
        : prev
    );
  };

  const handleCreateAddressLine2Change = (line2: string) => {
    setCreateState((prev) =>
      prev
        ? {
            ...prev,
            addressLine2: line2,
          }
        : prev
    );
  };

  const handleSaveEdit = async () => {
    if (!editState || addressLoading) return;
    const postalCodes = editState.postalCodesText
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    const processingMinutes = Number.parseInt(
      editState.processingMinutesText,
      10
    );
    if (Number.isNaN(processingMinutes) || processingMinutes < 0) {
      toast.error("Validation", {
        description:
          "Processing time must be a non-negative number of minutes.",
      });
      return;
    }
    // Drop runs with missing times before save (server rejects malformed runs).
    const runSchedule = editState.runSchedule
      ? {
          ...editState.runSchedule,
          weekly: Object.fromEntries(
            Object.entries(editState.runSchedule.weekly).map(([day, runs]) => [
              day,
              runs.filter((r) => r.departs && r.returns),
            ])
          ) as DispatchRunSchedule["weekly"],
        }
      : null;
    await updateMutation.mutateAsync({
      id: editState.id,
      data: {
        ...(editState.replacementAddress
          ? {
              address: {
                ...editState.replacementAddress,
                line2: editState.addressLine2 || null,
              },
            }
          : editState.address &&
              editState.addressLine2 !== (editState.address.line2 ?? "")
            ? { address: { line2: editState.addressLine2 || null } }
            : {}),
        name: editState.name,
        postalCodes,
        isActive: editState.isActive,
        processingMinutes,
        runSchedule,
      },
    });
  };

  const beginEdit = (dc: DispatchCenterListItem) => {
    setEditState({
      id: dc.id,
      providerCarrierId: dc.pickupProviderCarrier.id,
      address: dc.address,
      replacementAddress: null,
      addressLine2: dc.address?.line2 ?? "",
      name: dc.name,
      postalCodesText: dc.postalCodes.join(", "),
      isActive: dc.isActive,
      processingMinutesText: String(dc.processingMinutes ?? 120),
      runSchedule: dc.runSchedule ?? null,
    });
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const editAddress = editState?.replacementAddress ?? editState?.address;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Page header — same pattern as carriers-list.tsx (p-4 wrapper, bold
          title + muted description, primary action on the right). */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6" />
            Dispatch Centers
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            TooEZ-platform sortation facilities operated by local-fleet
            carriers. Pickups land here before redistribution to destination
            carriers.
          </p>
        </div>
        <Button
          onClick={() => {
            setCreateAttempted(false);
            setCodeConflict(null);
            setCreateFailure(null);
            setCreateState(DEFAULT_CREATE);
          }}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add dispatch
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            {dispatches?.length ?? 0} dispatch
            {dispatches?.length === 1 ? "" : "es"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code / name</TableHead>
                <TableHead>Operator</TableHead>
                <TableHead>Service area</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Postal codes</TableHead>
                <TableHead>Estimates</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(dispatches ?? []).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    No dispatch centers yet.
                  </TableCell>
                </TableRow>
              ) : (
                (dispatches ?? []).map((dc) => (
                  <TableRow key={dc.id}>
                    <TableCell>
                      <div className="text-sm font-medium">{dc.code}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {dc.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {dc.pickupProviderCarrier.name}
                    </TableCell>
                    <TableCell className="text-sm">
                      {dc.serviceArea.code}
                    </TableCell>
                    <TableCell className="text-sm">
                      {dc.address ? (
                        <>
                          <div>{dc.address.line1}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {dc.address.city}, {dc.address.province}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {dc.postalCodes.length === 0
                        ? "—"
                        : `${dc.postalCodes.length} FSAs`}
                    </TableCell>
                    <TableCell>
                      {/* Config-completeness warning: estimates silently
                          disable without a run schedule. */}
                      {dc.runSchedule ? (
                        <Badge
                          variant="default"
                          className="text-[10px] px-1.5 py-0"
                        >
                          On
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-700 gap-1"
                        >
                          <TriangleAlert className="h-2.5 w-2.5" />
                          Off — no run schedule
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={dc.isActive ? "default" : "secondary"}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {dc.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          aria-label={`Edit ${dc.name}`}
                          onClick={() => beginEdit(dc)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(dc.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog
        open={!!createState}
        onOpenChange={(o) => !o && setCreateState(null)}
      >
        <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add dispatch center</DialogTitle>
          </DialogHeader>

          {createState && (
            <div className="space-y-3 py-2">
              {createAttempted && Object.values(createErrors).some(Boolean) && (
                <Alert variant="destructive">
                  <AlertTitle>
                    {codeConflict?.code === createState.code.trim()
                      ? "Correct the code:"
                      : "Complete the missing setup:"}
                  </AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-5">
                      {Object.entries(createErrors).map(
                        ([field, message]) =>
                          message && (
                            <li key={field} id={`create-${field}-error`}>
                              {message}
                            </li>
                          )
                      )}
                    </ul>
                    {codeConflict?.code === createState.code.trim() &&
                      allDispatches?.some(
                        (dc) => dc.code === codeConflict.code
                      ) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const existing = allDispatches?.find(
                              (dc) => dc.code === codeConflict.code
                            );
                            if (!existing) return;
                            setCreateState(null);
                            beginEdit(existing);
                          }}
                        >
                          Edit existing center
                        </Button>
                      )}
                  </AlertDescription>
                </Alert>
              )}
              {createFailure && (
                <Alert variant="destructive">
                  <AlertTitle>Create failed</AlertTitle>
                  <AlertDescription>{createFailure}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  After configuring supply or service areas in another tab,
                  refresh the choices here. Your draft will be kept.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    carriersQuery.isFetching || serviceAreasQuery.isFetching
                  }
                  onClick={() => {
                    void carriersQuery.refetch();
                    void serviceAreasQuery.refetch();
                  }}
                >
                  Refresh setup choices
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="create-code" className="text-xs">
                    Code
                  </Label>
                  <Input
                    id="create-code"
                    aria-invalid={createAttempted && !!createErrors.code}
                    aria-describedby={
                      createAttempted && createErrors.code
                        ? "create-code-help create-code-error"
                        : "create-code-help"
                    }
                    value={createState.code}
                    onChange={(e) => {
                      setCodeConflict(null);
                      setCreateState({
                        ...createState,
                        code: e.target.value,
                      });
                    }}
                    placeholder="tooez_markham_01"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="create-name" className="text-xs">
                    Name
                  </Label>
                  <Input
                    id="create-name"
                    aria-invalid={createAttempted && !!createErrors.name}
                    aria-describedby={
                      createAttempted && createErrors.name
                        ? "create-name-error"
                        : undefined
                    }
                    value={createState.name}
                    onChange={(e) =>
                      setCreateState({
                        ...createState,
                        name: e.target.value,
                      })
                    }
                    placeholder="Toronto Dispatch"
                  />
                </div>
                <p
                  id="create-code-help"
                  className="col-span-2 text-xs text-muted-foreground"
                >
                  Code must be unique across all dispatch centers. Use 1–50
                  English letters, numbers, underscores or hyphens, with no
                  spaces. Recommended format: <code>carrier_city_number</code>,
                  for example <code>tooez_markham_01</code>.
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="create-operator" className="text-xs">
                  Operator carrier (self-rated)
                </Label>
                <Select
                  value={createState.operatorCarrierId}
                  onValueChange={(v) =>
                    setCreateState({
                      ...createState,
                      operatorCarrierId: v,
                    })
                  }
                >
                  <SelectTrigger
                    id="create-operator"
                    aria-invalid={createAttempted && !!createErrors.operator}
                    aria-describedby={
                      createAttempted && createErrors.operator
                        ? "create-operator-error"
                        : undefined
                    }
                  >
                    <SelectValue placeholder="Pick an operator…" />
                  </SelectTrigger>
                  <SelectContent>
                    {selfRatedCarriers.length === 0 ? (
                      <SelectItem value="none" disabled>
                        {carriersQuery.isPending
                          ? "Loading carriers…"
                          : carriersQuery.isError
                            ? "Unable to load carriers"
                            : "No self-rated carriers — configure carrier supply first"}
                      </SelectItem>
                    ) : (
                      selfRatedCarriers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} ({c.code})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {carriersQuery.isSuccess && selfRatedCarriers.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No self-rated carriers available. An admin must enable carrier
                  supply before it can operate a dispatch center.{" "}
                  <a
                    className="underline"
                    href="/carriers/list"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Configure carrier supply
                  </a>
                </p>
              )}
              {carriersQuery.isPending && (
                <p role="status" className="text-sm">
                  Loading operator carriers…
                </p>
              )}
              {carriersQuery.isError && (
                <p className="text-sm text-destructive">
                  Unable to load operator carriers.
                </p>
              )}
              {carriersQuery.isError && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void carriersQuery.refetch()}
                >
                  Retry loading carriers
                </Button>
              )}

              <div className="space-y-1">
                <Label className="text-xs">Address</Label>
                <DispatchAddressSearch
                  disabled={isPending}
                  onSelect={handleCreateAddressChange}
                  onLoadingChange={setAddressLoading}
                />
                <p className="text-sm">
                  {[
                    createState.addressLine1,
                    createState.addressCity,
                    createState.addressProvince,
                    createState.addressPostalCode,
                  ]
                    .filter(Boolean)
                    .join(", ") || "Select an address to set its map location."}
                </p>
                <Label htmlFor="create-address-unit">Unit / suite</Label>
                <Input
                  id="create-address-unit"
                  value={createState.addressLine2}
                  onChange={(event) =>
                    handleCreateAddressLine2Change(event.target.value)
                  }
                  disabled={isPending || !createState.addressLocation}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="create-area" className="text-xs">
                  Service area (auto-derived from postal-code FSA, override if
                  needed)
                </Label>
                <Select
                  value={createState.serviceAreaId}
                  onValueChange={(v) =>
                    setCreateState({ ...createState, serviceAreaId: v })
                  }
                >
                  <SelectTrigger
                    id="create-area"
                    aria-invalid={createAttempted && !!createErrors.area}
                    aria-describedby={
                      createAttempted && createErrors.area
                        ? "create-area-error"
                        : undefined
                    }
                  >
                    <SelectValue placeholder="Pick a service area…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(serviceAreas ?? []).map((sa) => (
                      <SelectItem key={sa.id} value={sa.id}>
                        {sa.name} ({sa.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {serviceAreasQuery.isPending && (
                <p role="status" className="text-sm">
                  Loading service areas…
                </p>
              )}
              {serviceAreasQuery.isError && (
                <div className="space-y-2">
                  <p className="text-sm text-destructive">
                    Unable to load service areas.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void serviceAreasQuery.refetch()}
                  >
                    Retry loading service areas
                  </Button>
                </div>
              )}
              {serviceAreasQuery.isSuccess && serviceAreas?.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No service areas available.{" "}
                  <a
                    className="underline"
                    href="/service-areas"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Manage service areas
                  </a>
                </p>
              )}

              <p className="text-xs text-muted-foreground">
                This center has one service area. Carrier coverage can include
                multiple areas; sender postal codes below configure this
                center’s pickup coverage.
              </p>

              <div className="space-y-1">
                <Label className="text-xs">
                  Sender postal codes / FSAs (comma-separated)
                </Label>
                <Input
                  value={createState.postalCodesText}
                  onChange={(e) =>
                    setCreateState({
                      ...createState,
                      postalCodesText: e.target.value,
                    })
                  }
                  placeholder="M5A, M5B, M5C…"
                />
                <p className="text-[10px] text-muted-foreground">
                  Senders with these postal codes/FSAs will be routed to this
                  dispatch.
                </p>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Switch
                  checked={createState.isActive}
                  onCheckedChange={(v) =>
                    setCreateState({ ...createState, isActive: v })
                  }
                />
                <Label className="text-xs">Active</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateState(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isPending || addressLoading}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editState} onOpenChange={(o) => !o && setEditState(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit dispatch center</DialogTitle>
          </DialogHeader>

          {editState && (
            <div className="space-y-3 py-2">
              <p className="text-[10px] text-muted-foreground">
                Code, operator, and service area are fixed. Select a replacement
                address below to update this center’s map location.
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  {[
                    editAddress?.line1,
                    editAddress?.city,
                    editAddress?.province,
                    editAddress?.postalCode,
                  ]
                    .filter(Boolean)
                    .join(", ") || "No address saved"}
                </p>
                <DispatchAddressSearch
                  disabled={isPending}
                  onLoadingChange={setAddressLoading}
                  onSelect={(address) =>
                    setEditState((previous) =>
                      previous
                        ? {
                            ...previous,
                            replacementAddress: address,
                            addressLine2: address.line2,
                          }
                        : previous
                    )
                  }
                />
                <Label htmlFor="edit-address-unit">Unit / suite</Label>
                <Input
                  id="edit-address-unit"
                  value={editState.addressLine2}
                  disabled={
                    isPending ||
                    (!editState.address && !editState.replacementAddress)
                  }
                  onChange={(event) =>
                    setEditState({
                      ...editState,
                      addressLine2: event.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-name" className="text-xs">
                  Name
                </Label>
                <Input
                  id="edit-name"
                  value={editState.name}
                  onChange={(e) =>
                    setEditState({ ...editState, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  Sender postal codes / FSAs (comma-separated)
                </Label>
                <Input
                  value={editState.postalCodesText}
                  onChange={(e) =>
                    setEditState({
                      ...editState,
                      postalCodesText: e.target.value,
                    })
                  }
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  checked={editState.isActive}
                  onCheckedChange={(v) =>
                    setEditState({ ...editState, isActive: v })
                  }
                />
                <Label className="text-xs">Active</Label>
              </div>

              {/* Delivery estimates config */}
              <div className="space-y-3 border-t pt-3">
                <div>
                  <Label className="text-sm font-medium">
                    Delivery estimates
                  </Label>
                  <p className="text-[10px] text-muted-foreground">
                    Drives the "at carrier by" / "delivered by" times customers
                    see on pricing options. Estimates, not promises.
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Processing time (minutes)</Label>
                  <Input
                    type="number"
                    min={0}
                    className="w-32"
                    value={editState.processingMinutesText}
                    onChange={(e) =>
                      setEditState({
                        ...editState,
                        processingMinutesText: e.target.value,
                      })
                    }
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Pickup slot end → packages back at this dispatch, ready for
                    the next run (driver return + sort).
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    Run schedule (internal trips)
                  </Label>
                  <RunScheduleEditor
                    value={editState.runSchedule}
                    onChange={(runSchedule) =>
                      setEditState({ ...editState, runSchedule })
                    }
                  />
                </div>
              </div>

              {/* Default drop-off locations (per label carrier) */}
              <div className="space-y-3 border-t pt-3">
                <div>
                  <Label className="text-sm font-medium">
                    Default drop-off locations
                  </Label>
                  <p className="text-[10px] text-muted-foreground">
                    Where packages picked up into this dispatch center get
                    dropped, per label carrier. Drives auto-assignment on
                    carrier drop-off tasks + the at-carrier estimate. Saved
                    immediately on change.
                  </p>
                </div>
                <DispatchCenterDropoffConfig
                  dispatchCenterId={editState.id}
                  providerCarrierId={editState.providerCarrierId}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditState(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isPending || addressLoading}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete dispatch center</AlertDialogTitle>
            <AlertDialogDescription>
              Server will refuse if rate bands still reference this dispatch —
              delete those first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) deleteMutation.mutate(deleteId);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
